import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Trophy, Gift, ChevronRight, Coins, ArrowLeft } from 'lucide-react';
import { getCustomer, getCustomerVisits, updateCustomerPoints } from '../services';
import { Visit, Reward, Customer } from '../types';
import { useEstablishmentStore } from '../store/establishment';

export default function PointsSummary() {
  const { phone } = useParams();
  const navigate = useNavigate();
  const { establishment } = useEstablishmentStore();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(5);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);

  // Get highest redeemable reward based on points
  const getRedeemableReward = () => {
    if (!establishment || !customer) return null;
    return establishment.settings.rewards
      .filter(reward => customer.points >= reward.pointsRequired && reward.active)
      .sort((a, b) => b.pointsRequired - a.pointsRequired)[0];
  };

  // Get next reward if any
  const getNextReward = () => {
    if (!establishment || !customer) return null;
    return establishment.settings.rewards
      .filter(reward => customer.points < reward.pointsRequired && reward.active)
      .sort((a, b) => a.pointsRequired - b.pointsRequired)[0];
  };

  useEffect(() => {
    if (!establishment || !phone) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const customerData = await getCustomer(establishment.id, phone);
        if (!customerData) {
          setError('Cliente não encontrado');
          return;
        }

        const visitsData = await getCustomerVisits(establishment.id, phone);
        
        setCustomer(customerData);
        setVisits(visitsData);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError(err.message || 'Erro ao carregar os dados');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [establishment, phone, navigate]);

  useEffect(() => {
    const redeemableReward = getRedeemableReward();
    if (loading || error || !customer || showRewardModal || redeemableReward) return;

    const timer = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    if (countdown === 0) {
      navigate('/', { replace: true });
    }

    return () => clearInterval(timer);
  }, [countdown, loading, error, customer, navigate, showRewardModal]);

  const handleRedeemReward = async (reward: Reward) => {
    if (!phone || !establishment || !customer) return;
    
    try {
      const remainingPoints = customer.points - reward.pointsRequired;
      await updateCustomerPoints(establishment.id, phone, remainingPoints);
      setCustomer(prev => prev ? { ...prev, points: remainingPoints } : null);
      setShowRewardModal(false);

      const nextAvailableReward = establishment.settings.rewards
        .filter(r => remainingPoints >= r.pointsRequired && r.active)
        .sort((a, b) => b.pointsRequired - a.pointsRequired)[0];
      
      if (!nextAvailableReward) {
        setCountdown(5);
      }
    } catch (err: any) {
      console.error('Error redeeming reward:', err);
      setError(err.message || 'Erro ao resgatar prêmio');
    }
  };

  const handleContinueAccumulating = () => {
    setShowRewardModal(false);
    setSelectedReward(null);
    navigate('/', { replace: true });
  };

  const handleReturnHome = () => {
    navigate('/', { replace: true });
  };

  if (!establishment) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
        <div className="card-container w-full max-w-md text-center">
          <p className="text-red-600 mb-4">{error || 'Cliente não encontrado'}</p>
          <button
            onClick={handleReturnHome}
            className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  const redeemableReward = getRedeemableReward();
  const nextReward = getNextReward();

  const formatPhone = (phone: string): string => {
    return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen gradient-bg flex items-center justify-center p-4">
      <div className="card-container w-full max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div>
            <div className="text-center mb-6">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-purple-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800">Parabéns!</h1>
              <p className="text-gray-600 mt-2">{customer.name}</p>
              <p className="text-gray-500">{formatPhone(customer.phone)}</p>
              {!redeemableReward && !showRewardModal && (
                <div className="mt-4">
                  <p className="text-sm text-gray-400 mb-2">
                    Voltando para a tela inicial em {countdown} segundos...
                  </p>
                  <button
                    onClick={handleReturnHome}
                    className="flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 mx-auto"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Voltar agora
                  </button>
                </div>
              )}
            </div>

            <div className="bg-purple-50 rounded-lg p-6 mb-6">
              <div className="text-center">
                <p className="text-4xl font-bold text-purple-600">{customer.points}</p>
                <p className="text-gray-600">pontos acumulados</p>
                <p className="text-sm text-gray-500 mt-2">
                  Total de visitas: {customer.totalVisits}
                </p>
                {customer.lastVisit && (
                  <p className="text-sm text-gray-500 mt-2">
                    Última visita: {formatDate(customer.lastVisit)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Available Reward */}
            {redeemableReward && !showRewardModal && (
              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-yellow-700 flex items-center">
                    <Gift className="w-5 h-5 mr-2" />
                    Prêmio Disponível
                  </h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-700">{redeemableReward.description}</p>
                      <p className="text-sm text-gray-500">{redeemableReward.pointsRequired} pontos</p>
                      <p className="text-sm text-green-600">
                        Saldo após resgate: {customer.points - redeemableReward.pointsRequired} pontos
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedReward(redeemableReward);
                        setShowRewardModal(true);
                      }}
                      className="btn-gradient px-4 py-2 rounded-lg text-white font-medium flex items-center"
                    >
                      Resgatar
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t-2 border-yellow-200">
                  <button
                    onClick={handleContinueAccumulating}
                    className="w-full flex items-center justify-center gap-2 bg-white border-2 border-purple-200 
                             text-purple-700 py-3 rounded-lg font-medium hover:bg-purple-50 transition-colors"
                  >
                    <Coins className="w-5 h-5" />
                    Continuar Acumulando
                  </button>
                </div>
              </div>
            )}

            {/* Next Reward */}
            {nextReward && (
              <div className="bg-gray-50 border-2 border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Próximo Prêmio</h3>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-gray-700">{nextReward.description}</p>
                    <p className="text-sm text-gray-500">
                      Faltam {nextReward.pointsRequired - customer.points} pontos
                    </p>
                  </div>
                  <div className="w-20 h-20 relative">
                    <svg className="transform -rotate-90" viewBox="0 0 80 80">
                      <circle
                        className="text-gray-200"
                        strokeWidth="5"
                        stroke="currentColor"
                        fill="transparent"
                        r="30"
                        cx="40"
                        cy="40"
                      />
                      <circle
                        className="text-purple-500"
                        strokeWidth="5"
                        strokeDasharray={2 * Math.PI * 30}
                        strokeDashoffset={2 * Math.PI * 30 * (1 - customer.points / nextReward.pointsRequired)}
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="transparent"
                        r="30"
                        cx="40"
                        cy="40"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-sm font-medium">
                        {Math.round((customer.points / nextReward.pointsRequired) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Reward Confirmation Modal */}
        {showRewardModal && selectedReward && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Confirmar Resgate</h3>
              <p className="text-gray-600 mb-2">
                Deseja resgatar o prêmio "{selectedReward.description}"?
              </p>
              <p className="text-gray-600 mb-6">
                Pontos atuais: {customer.points}
                <br />
                Pontos após resgate: {customer.points - selectedReward.pointsRequired}
              </p>
              <div className="flex flex-col gap-4">
                <button
                  onClick={() => handleRedeemReward(selectedReward)}
                  className="btn-gradient text-white py-3 rounded-xl font-semibold"
                >
                  Confirmar Resgate
                </button>
                <button
                  onClick={handleContinueAccumulating}
                  className="bg-white border-2 border-purple-200 text-purple-700 py-3 rounded-xl font-semibold
                           hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Coins className="w-5 h-5" />
                  Continuar Acumulando
                </button>
                <button
                  onClick={() => {
                    setShowRewardModal(false);
                    setSelectedReward(null);
                  }}
                  className="bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold
                           hover:bg-gray-200 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}